import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const GREEN = '#16a34a';
const DARK = '#1e293b';
const GRAY = '#64748b';
const LIGHT = '#f1f5f9';
const BORDER = '#e2e8f0';
const WHITE = '#ffffff';

const styles = StyleSheet.create({
  page: { padding: 48, fontSize: 10, fontFamily: 'Helvetica', color: DARK },

  // Header
  headerBar: { backgroundColor: GREEN, borderRadius: 4, padding: 20, marginBottom: 24 },
  headerTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', color: WHITE },
  headerSub: { fontSize: 10, color: '#bbf7d0', marginTop: 4 },
  headerDate: { fontSize: 9, color: '#86efac', marginTop: 2 },

  // Intro
  intro: { fontSize: 9, color: GRAY, lineHeight: 1.6, marginBottom: 24 },

  // Section
  sectionHeader: {
    backgroundColor: LIGHT,
    borderRadius: 3,
    paddingVertical: 7,
    paddingHorizontal: 10,
    marginTop: 18,
    marginBottom: 8,
  },
  sectionTitle: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: DARK },
  sectionDesc: { fontSize: 9, color: GRAY, marginBottom: 8, lineHeight: 1.5 },

  // Key-value grid
  kvRow: { flexDirection: 'row', marginBottom: 5 },
  kvLabel: { width: 140, fontSize: 9, fontFamily: 'Helvetica-Bold', color: GRAY },
  kvValue: { flex: 1, fontSize: 9, color: DARK },

  // Table
  tableRow: { flexDirection: 'row' },
  tableHeaderCell: {
    backgroundColor: LIGHT,
    borderWidth: 0.5,
    borderColor: BORDER,
    paddingVertical: 5,
    paddingHorizontal: 6,
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: DARK,
  },
  tableCell: {
    borderWidth: 0.5,
    borderColor: BORDER,
    paddingVertical: 5,
    paddingHorizontal: 6,
    fontSize: 8,
    color: DARK,
    backgroundColor: WHITE,
  },
  empty: { fontSize: 9, color: GRAY, fontStyle: 'italic', marginBottom: 6 },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 48,
    right: 48,
    borderTopWidth: 0.5,
    borderTopColor: BORDER,
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: { fontSize: 7, color: GRAY },
});

const fmt = (d?: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

interface TableProps {
  headers: string[];
  rows: string[][];
  widths: number[]; // fractions that sum to 1
}

const Table = ({ headers, rows, widths }: TableProps) => (
  <View>
    <View style={styles.tableRow}>
      {headers.map((h, i) => (
        <Text key={i} style={[styles.tableHeaderCell, { flex: widths[i] }]}>{h}</Text>
      ))}
    </View>
    {rows.map((row, ri) => (
      <View key={ri} style={styles.tableRow} wrap={false}>
        {row.map((cell, ci) => (
          <Text key={ci} style={[styles.tableCell, { flex: widths[ci] }]}>{cell || '—'}</Text>
        ))}
      </View>
    ))}
  </View>
);

interface KVProps { label: string; value?: string | null }
const KV = ({ label, value }: KVProps) => (
  <View style={styles.kvRow}>
    <Text style={styles.kvLabel}>{label}</Text>
    <Text style={styles.kvValue}>{value || '—'}</Text>
  </View>
);

interface SectionProps { title: string; description: string }
const Section = ({ title, description }: SectionProps) => (
  <View>
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
    <Text style={styles.sectionDesc}>{description}</Text>
  </View>
);

export interface GDPRExportData {
  export_date: string;
  email: string;
  profile: {
    first_name?: string;
    last_name?: string;
    client_number?: string;
    status?: string;
    organization?: string;
    created_at?: string;
    last_login?: string;
  } | null;
  energy_consumption?: any[];
  bills?: any[];
  topups?: any[];
  contracts?: any[];
  notices?: any[];
  login_history?: any[];
}

interface Props { data: GDPRExportData }

const GDPRExportPDF = ({ data }: Props) => {
  const profile = data.profile ?? {};
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ') || '—';

  const billRows = (data.bills ?? []).map((b: any) => [
    fmt(b.bill_date ?? b.created_at),
    b.amount != null ? `${Number(b.amount).toFixed(2)} XAF` : '—',
    b.status ?? '—',
  ]);

  const topupRows = (data.topups ?? []).map((t: any) => [
    fmt(t.created_at),
    t.amount != null ? `${Number(t.amount).toFixed(2)} XAF` : '—',
    t.status ?? '—',
    t.reference ?? '—',
  ]);

  const consumptionRows = (data.energy_consumption ?? []).map((c: any) => [
    fmt(c.recorded_at ?? c.created_at),
    c.consumption_kwh != null ? `${Number(c.consumption_kwh).toFixed(2)} kWh` : '—',
  ]);

  const contractRows = (data.contracts ?? []).map((c: any) => [
    fmt(c.created_at),
    c.status ?? '—',
    c.type ?? '—',
  ]);

  const noticeRows = (data.notices ?? []).map((n: any) => [
    fmt(n.created_at),
    n.title ?? '—',
    n.message ?? '—',
  ]);

  const loginRows = (data.login_history ?? []).map((l: any) => [
    fmt(l.login_at ?? l.created_at),
    l.user_agent ?? '—',
    l.ip_address ?? '—',
  ]);

  return (
    <Document title="My Personal Data - Growth Energy">
      <Page size="A4" style={styles.page}>

        {/* Header */}
        <View style={styles.headerBar}>
          <Text style={styles.headerTitle}>My Personal Data</Text>
          <Text style={styles.headerSub}>Growth Energy</Text>
          <Text style={styles.headerDate}>Exported on {fmt(data.export_date)}</Text>
        </View>

        {/* Intro */}
        <Text style={styles.intro}>
          This document contains all the personal data Growth Energy holds about you, as required by
          GDPR Article 20 (Right to Data Portability). If you have questions, contact your building
          administrator or email support@growth-energy.fr
        </Text>

        {/* Profile */}
        <Section title="My Profile" description="Your account details as stored in our system." />
        <KV label="Full Name" value={fullName} />
        <KV label="Email Address" value={data.email} />
        <KV label="Client Number" value={profile.client_number} />
        <KV label="Account Status" value={profile.status ?? 'active'} />
        <KV label="Organization" value={profile.organization} />
        <KV label="Account Created" value={fmt(profile.created_at)} />
        <KV label="Last Login" value={fmt(profile.last_login)} />

        {/* Energy Usage */}
        <Section
          title="Energy Usage History"
          description="A record of your electricity consumption readings over time."
        />
        {consumptionRows.length > 0
          ? <Table headers={['Date', 'Usage']} rows={consumptionRows} widths={[0.55, 0.45]} />
          : <Text style={styles.empty}>No energy usage records found.</Text>
        }

        {/* Bills */}
        <Section title="My Bills" description="All bills that have been issued to your account." />
        {billRows.length > 0
          ? <Table headers={['Bill Date', 'Amount', 'Status']} rows={billRows} widths={[0.4, 0.3, 0.3]} />
          : <Text style={styles.empty}>No bills found.</Text>
        }

        {/* Top-ups */}
        <Section
          title="Top-Up History"
          description="Every time you added credit to your electricity account."
        />
        {topupRows.length > 0
          ? <Table headers={['Date', 'Amount', 'Status', 'Reference']} rows={topupRows} widths={[0.25, 0.2, 0.2, 0.35]} />
          : <Text style={styles.empty}>No top-up records found.</Text>
        }

        {/* Contracts */}
        <Section title="Contracts" description="Agreements between you and Growth Energy." />
        {contractRows.length > 0
          ? <Table headers={['Date', 'Status', 'Type']} rows={contractRows} widths={[0.4, 0.3, 0.3]} />
          : <Text style={styles.empty}>No contracts found.</Text>
        }

        {/* Notices */}
        <Section
          title="Notices Received"
          description="Messages and notices sent to you by your building management."
        />
        {noticeRows.length > 0
          ? <Table headers={['Date', 'Title', 'Message']} rows={noticeRows} widths={[0.2, 0.3, 0.5]} />
          : <Text style={styles.empty}>No notices found.</Text>
        }

        {/* Login history */}
        <Section
          title="Login History"
          description="A record of every time someone logged into your account (last 100 sessions)."
        />
        {loginRows.length > 0
          ? <Table headers={['Date', 'Device / Browser', 'IP Address']} rows={loginRows} widths={[0.22, 0.53, 0.25]} />
          : <Text style={styles.empty}>No login history found.</Text>
        }

        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Growth Energy — Confidential Personal Data</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          } />
        </View>

      </Page>
    </Document>
  );
};

export default GDPRExportPDF;
